import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";

import type { TFn } from "../i18nTypes";
import type { ChartSeriesAnnotation } from "./chartAnnotations";

// Split out of `WagmiLineChart.tsx` (the 500-line cap). The plot is one
// image to a screen reader and its tap layer is hidden from it, so with
// `showMeta={false}` (how every Progress row mounts the chart) it had NO
// accessible content at all, a screen reader saw nothing. This is the fix: a
// NON-VISUAL sibling of the plot.
//
// Antenore, 24/09: ONE node per SERIES, not one per point, each label
// walking all of that series' points in order, "; "-joined. Every point still
// says date · value · "N days since the previous point": `gapDays` is the
// only place a silence is stated (`chartAnnotations.ts` stopped cutting the
// line for it at PG-36), same source the old SVG renderer's label read, and
// nothing here drops it.
//
// PG-53: `accessibilityRole="text"` maps to NO web `role` at all
// (react-native-web's `accessibilityRoleToWebRole` table), so a screen
// reader on web could skip the node entirely. `"image"` maps to `role:
// "img"`, the standard for a chart, same role as the `plot` in
// `WagmiLineChart.tsx`. Not `"summary"`: on web it makes one landmark per
// series, and on iOS it is re-announced when the app returns to the
// foreground. With 2+ series AND a `name` on
// the series, the label is prefixed "<name>: <points>" so each node says
// which curve it is; one series, or a series with no name, keeps the plain
// label, nothing invented.
//
// Present in the tree (so a screen reader can reach it) but never seen or
// laid out: `position: "absolute"` pulls it out of flow, zero size keeps it
// from ever affecting the row's visual height. Checked with a raw
// `Accessibility.getFullAXTree` CDP call (Chromium): a 0x0 `overflow:
// hidden` node with a role and a name is NOT `ignored` there, so the classic
// sr-only 1x1 dance buys nothing here and the box stays 0x0.

/** One point's spoken name: date · value · "N days since the previous
 *  point". Shared with `BarChart.tsx` (PG-63), whose bars are its own
 *  buttons and name themselves with it. */
export function chartPointA11yLabel(t: TFn, label: string, value: string, gapDays?: number) {
  return [
    label,
    value,
    gapDays === undefined
      ? ""
      : t("progress.chartGapLong", {
          defaultValue: "{{count}} days since the previous point",
          count: gapDays,
        }),
  ]
    .filter(Boolean)
    .join(" · ");
}

// PG-54: a long series is not read out whole. Above A11Y_FULL_MAX points the
// label says the first A11Y_HEAD, «+N more points», then the last A11Y_TAIL,
// all real samples. Up to A11Y_FULL_MAX it says every point.
export const A11Y_FULL_MAX = 20;
export const A11Y_HEAD = 5;
export const A11Y_TAIL = 10;

type Props = {
  built: ChartSeriesAnnotation[];
  yFormat: (y: number) => string;
};

export function WagmiChartA11yPoints({ built, yFormat }: Props) {
  const { t } = useTranslation();
  const prefixNames = built.length >= 2;
  return (
    <View style={styles.a11yPoints} pointerEvents="none">
      {built.map((b) => {
        const say = (dots: typeof b.dots) =>
          dots.map((dot) => chartPointA11yLabel(t, dot.point.label, yFormat(dot.point.y), dot.gapDays));
        const n = b.dots.length;
        const points = (
          n <= A11Y_FULL_MAX
            ? say(b.dots)
            : [
                ...say(b.dots.slice(0, A11Y_HEAD)),
                t("progress.chartMorePoints", {
                  defaultValue: "+{{count}} more points",
                  count: n - A11Y_HEAD - A11Y_TAIL,
                }),
                ...say(b.dots.slice(n - A11Y_TAIL)),
              ]
        ).join("; ");
        const label = prefixNames && b.name ? `${b.name}: ${points}` : points;
        return <View key={`a11y-${b.key}`} accessibilityRole="image" accessibilityLabel={label} />;
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  a11yPoints: { position: "absolute", width: 0, height: 0, overflow: "hidden" },
});
